import type { components, paths } from '../schemas/data-dashboard';

type NonNullableKeys<T> = {
  [K in keyof T]-?: null extends T[K] ? never : K;
}[keyof T];
type NullableKeys<T> = {
  [K in keyof T]-?: null extends T[K] ? K : never;
}[keyof T];
type OptionalIfNullable<T> = { [K in NonNullableKeys<T>]: T[K] } & Partial<{
  [K in NullableKeys<T>]: T[K];
}>;
type TypeFromTaggedUnion<T, U> = T extends { function: U } ? T : never;

// -- Parameters --

export type ApiMeasureParams =
  paths['/measures']['put']['requestBody']['content']['application/json'];

export type ApiTransformationParams =
  paths['/measures']['post']['requestBody']['content']['application/json'];
export type ApiManualMeasuresAddition =
  paths['/manual_measures']['post']['requestBody']['content']['application/json'];

export type AggregationFunction = components['schemas']['Aggregate'];
export type AggregateFunctionParams =
  components['schemas']['Aggregate']['parameters']['aggregations'][number];
export type AggregateFunctionName = AggregateFunctionParams['function'];

export type AllPipelineFunctions =
  components['schemas']['PipelineParams']['functions'][number];
export type PipelineFunctionName = AllPipelineFunctions['function'];
export type PipelineFunctionFromName<T extends PipelineFunctionName> =
  TypeFromTaggedUnion<AllPipelineFunctions, T>;
export type PipelineFunctionParamsFromName<T extends PipelineFunctionName> =
  OptionalIfNullable<
    PipelineFunctionFromName<T> extends { parameters: infer P } ? P : never
  >;

type TypeFromFunctionName<T extends AggregateFunctionName> =
  TypeFromTaggedUnion<AggregateFunctionParams, T>;
type ParametersFromFunctionName<T extends AggregateFunctionName> =
  OptionalIfNullable<
    TypeFromFunctionName<T> extends { parameters: infer P } ? P : never
  >;

export type APIBodyParamsV2 = components['schemas']['ApiBodyParamsUnionV2V3'];
export type APIBodyParamsPythonOnly =
  components['schemas']['ApiBodyParamsV3PythonOnly'];
export type ApiTransformationParamsV2 =
  paths['/measures/v2']['post']['requestBody']['content']['application/json'];

export type ApplyThresholds = components['schemas']['ApplyThresholds'];
export type ApplyThresholdOptions =
  components['schemas']['ApplyThresholdOptions'];
export type ThresholdRequest = components['schemas']['ThresholdRequest'];
export type ThresholdDomainParams =
  components['schemas']['ThresholdDomainParams'];
export type ThresholdRepresentation =
  components['schemas']['ThresholdRepresentation'];
export type ThresholdStatusItem = components['schemas']['ThresholdStatusItem'];
export type ThresholdsResult = components['schemas']['ThresholdsResult'];
export type ThresholdBreachesResult =
  components['schemas']['ThresholdBreachesResult'];
export type ThresholdBreachReport =
  components['schemas']['ThresholdBreachReport_Any_'];
export type BreachItem = components['schemas']['BreachItem_Any_'];
export type AxisDomain = components['schemas']['AxisDomain'];
export type GeometryWrapper = components['schemas']['GeometryWrapper'];

// -- Responses --

export type ApiReturnMeasuresValidated =
  components['schemas']['ApiReturnMeasuresValidated'];

export type ApiTransformationResponse = components['schemas']['APIReturnsData'];
export type ApiTransformationResponseV2 =
  components['schemas']['APIReturnsDataV2'];
export type ApiTransformationDataRowV2 =
  ApiTransformationResponseV2['data'][number];

export type ResponseMetadata = components['schemas']['ResponseMetadata'];
export type ContextTimezoneInfo = components['schemas']['ContextTimezoneInfo'];
export type HeatmapMetadata = components['schemas']['HeatmapMetadata'];
export type HeatmapAxisMetadata = components['schemas']['HeatmapAxisMetadata'];
export type HeatmapPivotAxisMetadata =
  components['schemas']['HeatmapPivotAxisMetadata'];
export type HeatmapPivotEntry = components['schemas']['HeatmapPivotEntry'];

export type ApiDataDashboardHTTPValidationError =
  components['schemas']['HTTPValidationError'];
export type ApiDataDashboardValidationError =
  components['schemas']['ValidationError'];

// -- Helpers --

// --- Manual Measures API ---

export type TimestampedMeasure = ApiManualMeasuresAddition['values'][number];

// --- Transformation API ---

type ConditionTypeEnum =
  | 'BetweenCondition'
  | 'LessThanCondition'
  | 'LessThanOrEqualCondition'
  | 'EqualCondition'
  | 'GreaterThanOrEqualCondition'
  | 'GreaterThanCondition'
  | 'NotEqualCondition';

const condition_tag_map: {
  [KEY in ConditionTypeEnum]: components['schemas'][KEY]['condition'];
} = {
  BetweenCondition: 'between',
  LessThanCondition: 'less_than',
  LessThanOrEqualCondition: 'less_than_or_equal',
  EqualCondition: 'equal',
  GreaterThanOrEqualCondition: 'greater_than_or_equal',
  GreaterThanCondition: 'greater_than',
  NotEqualCondition: 'not_equal',
};

export function tr_function<T extends PipelineFunctionName>(
  function_name: T,
  parameters: PipelineFunctionParamsFromName<T>
) {
  return {
    parameters,
    function: function_name,
  } as unknown as PipelineFunctionFromName<T>;
}

export function tr_aggregation_function<T extends AggregateFunctionName>(
  functionName: T,
  params: ParametersFromFunctionName<T>
) {
  return {
    function: functionName,
    parameters: params,
  } as unknown as TypeFromTaggedUnion<AggregateFunctionParams, T>;
}

export function tr_condition<ReturnKeyType extends ConditionTypeEnum>(
  condition_type: ReturnKeyType,
  column: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any
) {
  return {
    column: column,
    condition: condition_tag_map[condition_type],
    ...params,
  };
}

export function tr_not_equal(
  column: components['schemas']['NotEqualCondition']['column'],
  value: components['schemas']['NotEqualCondition']['value']
): components['schemas']['Filter'] {
  return {
    function: 'filter',
    parameters: {
      condition: [
        {
          column,
          value,
          condition: 'not_equal',
        },
      ],
    },
  };
}

export function tr_groupby(
  group_column_name: components['schemas']['GroupBy']['parameters']['group_column_name'],
  categorisation: components['schemas']['GroupBy']['parameters']['categorisation']
): components['schemas']['GroupBy'] {
  return {
    function: 'group_by',
    parameters: { group_column_name, categorisation },
  };
}

export function tr_categorisation(
  group_name: components['schemas']['Categorisation']['group_name'],
  condition: components['schemas']['Categorisation']['condition']
): components['schemas']['Categorisation'] {
  return { group_name, condition };
}

export function tr_aggregate(
  by_columns: components['schemas']['Aggregate']['parameters']['by_columns'],
  aggregations: components['schemas']['Aggregate']['parameters']['aggregations']
): components['schemas']['Aggregate'] {
  return tr_function('aggregate', { by_columns, aggregations });
}
